// components/TableViewer.jsx
import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { FixedSizeList as List } from 'react-window';

const INFO_COL_WIDTH = 30;

const TableCell = React.memo(({ value, columnWidth, searchFilter, header }) => {
  const cellText = String(value ?? '');
  const isMatched = searchFilter && value !== null && cellText.toLowerCase().includes(searchFilter);
  const h = header.toLowerCase();

  const renderContent = () => {
    if (!value || cellText.trim() === '') return cellText;
    const linkClass = "text-blue-600 hover:text-blue-800 hover:underline decoration-blue-400 font-medium";
    const stopProp = (e) => e.stopPropagation();

    let singleHref = null;
    const cleanText = cellText.trim();
    if (h === 'pmid') { singleHref = `https://pubmed.ncbi.nlm.nih.gov/${cleanText}/`; } 
    else if (h === 'primary_id') {
      if (/^SAM(D|EA|N)/i.test(cleanText)) singleHref = `https://www.ncbi.nlm.nih.gov/biosample/${cleanText}`;
      else if (/^SAMC/i.test(cleanText)) singleHref = `https://ngdc.cncb.ac.cn/biosample/browse/${cleanText}`;
    } 
    else if (h === 'secondary_id') {
          if (cleanText.startsWith('IMGVR')) {
            singleHref = `https://www.meta-virome.org/uviginfo/${cleanText}`;
          } else {
            singleHref = `https://www.ncbi.nlm.nih.gov/nuccore/${cleanText}`;
          }
    }
    else if (h === 'virus_taxid') { singleHref = `https://www.ncbi.nlm.nih.gov/datasets/taxonomy/${cleanText}/`; } 
    else if (h === 'run') {
      if (cleanText.startsWith('CRA')) { singleHref = `https://ngdc.cncb.ac.cn/gsa/browse/${cleanText}`; } 
      else { singleHref = `https://www.ncbi.nlm.nih.gov/sra/?term=${cleanText}`; }
    }

    if (singleHref) {
      return <a href={singleHref} target="_blank" rel="noopener noreferrer" className={linkClass} onClick={stopProp}>{cellText}</a>;
    }

    if (h === 'envo_id' || h === 'uberon_id' || h === 'mondo_id'|| h === 'host_taxid' || h === 'environment_taxid') {
      const parts = cellText.split('|');
      return parts.map((part, index) => {
        const trimmedPart = part.trim();
        let href = '';
        if (h === 'host_taxid' || h === 'environment_taxid') {
          const cleanTaxId = trimmedPart.replace(/\.0$/, '');
          href = `https://www.ncbi.nlm.nih.gov/datasets/taxonomy/${cleanTaxId}`;
         } else if (h === 'mondo_id') {
          const urlId = trimmedPart.replace(':', '_');
          href = `https://www.ebi.ac.uk/ols4/ontologies/mondo/classes/http%253A%252F%252Fpurl.obolibrary.org%252Fobo%252F${urlId}`;
        } else {
          const urlId = trimmedPart.replace(':', '_');
          href = `http://purl.obolibrary.org/obo/${urlId}`;
        }
        return (
          <React.Fragment key={index}>
            <a href={href} target="_blank" rel="noopener noreferrer" className={linkClass} onClick={stopProp}>{trimmedPart}</a>
            {index < parts.length - 1 && <span className="text-gray-400 mx-0.5">|</span>}
          </React.Fragment>
        );
      });
    }

    const urlRegex = /(https?:\/\/[^\s]+)/gi;
    if (cellText.match(urlRegex)) {
      return cellText.split(urlRegex).map((part, index) => {
        if (/^https?:\/\//i.test(part)) {
          return <a key={index} href={part} target="_blank" rel="noopener noreferrer" className={linkClass} onClick={stopProp}>{part}</a>;
        }
        return part;
      });
    }
    return cellText;
  };

  return (
    <div
      style={{ width: columnWidth, minWidth: columnWidth }}
      className={`px-2 py-1 truncate border-r border-gray-200 text-sm transition-colors select-text cursor-text ${isMatched ? 'bg-yellow-200/60 font-medium text-yellow-900' : ''}`}
    >
      {renderContent()}
    </div>
  );
});

const Row = React.memo(({ index, style, data }) => {
  const { isEmpty, table, headers, filteredIndices, columnWidths, searchFilter, totalWidth, onRowClick } = data;
  const realIndex = filteredIndices[index];

  if (isEmpty) {
    return (
      <div style={{ ...style, width: totalWidth }} className="flex items-center ml-16 mt-16 text-gray-500 text-lg">
        No data matches the currently active filters and/or search term.
      </div>
    );
  }

  const sourceValue = table.getChild('source')?.get(realIndex);
  const isViralGenbank = sourceValue === 'Viral Genbank';
  const isBiosample = sourceValue === 'NCBI BioSample' || sourceValue === 'GSA BioSample';

  const handleInfoClick = (e) => {
    e.stopPropagation();
    if (onRowClick) {
      onRowClick(table.get(realIndex), realIndex); 
    }
  };

  return (
    <div 
      style={{ ...style, width: totalWidth }} 
      className={`flex border-b border-gray-200 ${index % 2 === 1 ? 'bg-gray-50' : 'bg-white'} hover:bg-blue-50 group`}
    >
        {/* Native zero-lag Sticky Info Column */}
        <div 
          className={`sticky left-0 bg-inherit border-r border-gray-400 flex items-center justify-center cursor-pointer z-10 ${
  isViralGenbank ? 'text-orange-500 hover:text-orange-600' :  isBiosample ? 'text-green-500 hover:text-green-600' : 'text-blue-500 hover:text-blue-600'
} transition-colors flex-shrink-0`}
          style={{ width: INFO_COL_WIDTH, minWidth: INFO_COL_WIDTH }}
          onClick={handleInfoClick}
          title="View sample details"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        </div>

        {headers.map(header => {
            let displayValue = table.getChild(header).get(realIndex);
            if (/date/i.test(header) && displayValue !== null && displayValue !== undefined && header.toLowerCase() !== 'has_collection_date' && header.toLowerCase() !== 'has_valid_collection_date' && header.toLowerCase() !== 'is_validated') {
                const date = new Date(displayValue);
                if (!isNaN(date.getTime())) { displayValue = date.toISOString().split('T')[0]; }
            }
            if (/geometry/i.test(header)) {
                const geoJsonChild = table.getChild('_geojson_str');
                if (geoJsonChild) {
                    try {
                        const geo = JSON.parse(geoJsonChild.get(realIndex));
                        displayValue = geo.type === 'Point' 
                          ? `Point(${geo.coordinates[0].toFixed(4)}, ${geo.coordinates[1].toFixed(4)})` : geo.type;
                    } catch (e) { displayValue = ''; }
                }
            }
            return <TableCell key={header} header={header} value={displayValue} columnWidth={columnWidths[header]} searchFilter={searchFilter} />;
        })}
    </div>
  );
});

// Custom inner element seamlessly houses the Header and the list's rows
const InnerElement = React.forwardRef(({ children, style, ...rest }, ref) => {
  const childArray = React.Children.toArray(children);
  const itemData = childArray[0]?.props?.data; 

  if (!itemData) return <div ref={ref} style={style} {...rest}>{children}</div>;

  const { 
    headers, columnWidths, searchColumns, columnsWithMatches, 
    onToggleColumn, onHeaderMouseDown, totalWidth, INFO_COL_WIDTH 
  } = itemData;

  return (
    <div ref={ref} style={{ ...style, height: style.height + 24, width: totalWidth, minWidth: '100%' }} {...rest}>
      <div className="sticky top-0 z-30 flex bg-gray-200 font-semibold border-b border-gray-300" style={{ height: 24, width: totalWidth }}>
        <div className="sticky left-0 bg-white border-r border-gray-400 flex-shrink-0 z-40" style={{ width: INFO_COL_WIDTH, minWidth: INFO_COL_WIDTH }} />
        {headers.map(header => {
          const isSelected = searchColumns?.includes(header);
          const hasMatch = columnsWithMatches?.has(header);
          return (
            <div 
              key={header} 
              style={{ width: columnWidths[header], minWidth: columnWidths[header] }} 
              className={`relative px-2 py-1 truncate border-r border-gray-300 last:border-r-0 group cursor-pointer select-none transition-colors 
                ${isSelected ? 'bg-blue-200 text-blue-800 hover:bg-blue-300' : 'hover:bg-gray-300'}
                ${hasMatch ? 'ring-2 ring-inset ring-yellow-400 ' : ''}`}
              onClick={() => onToggleColumn(header)}
            >
              {header}
              <div 
                onMouseDown={(e) => onHeaderMouseDown(header, e)}
                onClick={(e) => e.stopPropagation()} 
                className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent z-50" 
              />
            </div>
          );
        })}
      </div>
      <div style={{ position: 'absolute', top: 24, left: 0, right: 0 }}>
        {children}
      </div>
    </div>
  );
});

const TableViewer = React.memo(function TableViewer({ data, onToggleColumn, onRowClick, basicMode=false }) {
  const { table, headers, searchFilter, searchColumns, searchIndices } = data;
  const containerRef = useRef(null);
  const listRef = useRef(null);
  

  const [scrollTop, setScrollTop] = useState(0);
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const filteredIndices = useMemo(() => {
    if (!table) return [];
    if (searchIndices === null) return Array.from({ length: table.numRows }, (_, i) => i);
    return searchIndices;
  }, [table, searchIndices]);

  const ITEM_SIZE = 24;
  const LIST_HEIGHT = viewportSize.height;
  const totalContentHeight = filteredIndices.length * ITEM_SIZE;

  // Thumb calculations mathematically constrained to exact max depths
  const maxScrollTop = Math.max(0, (totalContentHeight + 24) - LIST_HEIGHT);
  const thumbHeight = Math.max(20, LIST_HEIGHT > 0 ? (LIST_HEIGHT / (totalContentHeight + 24)) * LIST_HEIGHT : 0);
  const maxThumbTop = Math.max(0, LIST_HEIGHT - thumbHeight);
  const thumbTop = maxScrollTop > 0 ? (scrollTop / maxScrollTop) * maxThumbTop : 0;

  const onScroll = useCallback(({ scrollOffset }) => setScrollTop(scrollOffset), []);

  const columnsWithMatches = useMemo(() => {
    if (!searchFilter || filteredIndices.length === 0) return new Set();
    const matchedSet = new Set();
    const colsToScan = headers.filter(h => !/geometry/i.test(h));
    for (const header of colsToScan) {
      const column = table.getChild(header);
      for (const idx of filteredIndices) {
        if (idx === -1) continue; 
        const val = column.get(idx);
        if (val !== null && String(val).toLowerCase().includes(searchFilter)) {
          matchedSet.add(header); break; 
        }
      }
    }
    return matchedSet;
  }, [table, filteredIndices, searchFilter, searchColumns, headers]);

  const [columnWidths, setColumnWidths] = useState(() => headers.reduce((acc, header) => ({ ...acc, [header]: 60 }), {}));
  const resizingColumn = useRef(null);
  const [isManuallyResized, setIsManuallyResized] = useState(false);

  useEffect(() => { setIsManuallyResized(false); }, [headers]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || headers.length === 0 || isManuallyResized) return;
    const observer = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      if (width <= 0 || height <= 0) return;
      
      setViewportSize({ width, height });
      const adaptiveWidth = Math.max(60, (width - INFO_COL_WIDTH) / headers.length);
      
      setColumnWidths(() => {
        const newWidths = {};
        headers.forEach(h => { newWidths[h] = adaptiveWidth; });
        return newWidths;
      });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [headers, isManuallyResized]);

  const handleThumbMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    const startY = e.pageY;
    const startScrollTop = scrollTop;
    
    // Grab the latest height from the state value inside the callback
    const currentMaxScrollTop = Math.max(0, (totalContentHeight + 24) - viewportSize.height);
    const currentThumbHeight = Math.max(20, (viewportSize.height / (totalContentHeight + 24)) * viewportSize.height);
    const currentMaxThumbTop = Math.max(0, viewportSize.height - currentThumbHeight);

    const onMouseMove = (moveEvent) => {
      const deltaY = moveEvent.pageY - startY;
      const scrollFraction = currentMaxThumbTop > 0 ? deltaY / currentMaxThumbTop : 0;
      const newScrollTop = startScrollTop + (scrollFraction * currentMaxScrollTop);
      if (listRef.current) listRef.current.scrollTo(Math.max(0, Math.min(currentMaxScrollTop, newScrollTop)));
    };
    const onMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [scrollTop, totalContentHeight, viewportSize.height]);

  const onMouseDown = useCallback((header, e) => {
    e.preventDefault();
    setIsManuallyResized(true); 
    resizingColumn.current = { header, startWidth: columnWidths[header], startX: e.pageX };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.cursor = 'col-resize';
  }, [columnWidths]);

  const onMouseMove = useCallback((e) => {
    if (!resizingColumn.current) return;
    const { header, startWidth, startX } = resizingColumn.current;
    const delta = e.pageX - startX;
    setColumnWidths(prev => ({ ...prev, [header]: Math.max(50, startWidth + delta) }));
  }, []);

  const onMouseUp = useCallback(() => {
    resizingColumn.current = null;
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    document.body.style.cursor = 'default';
  }, [onMouseMove]);

  useEffect(() => {
    return () => { document.removeEventListener('mousemove', onMouseMove); document.removeEventListener('mouseup', onMouseUp); };
  }, [onMouseMove]);

  const totalWidth = useMemo(() => headers.reduce((sum, h) => sum + (columnWidths[h] || 150), INFO_COL_WIDTH), [headers, columnWidths]);

  const itemData = useMemo(() => ({ 
    table, headers, columnWidths, searchFilter, totalWidth, searchColumns, onRowClick,
    filteredIndices: filteredIndices.length === 0 ? [-1] : filteredIndices,
    isEmpty: filteredIndices.length === 0,
    onHeaderMouseDown: onMouseDown, 
    INFO_COL_WIDTH,
    onToggleColumn,
    columnsWithMatches
  }), [
    table, headers, filteredIndices, columnWidths, searchFilter, totalWidth, 
    searchColumns, onRowClick, onMouseDown, 
    onToggleColumn,
    columnsWithMatches
  ]);

  return (
    <>
    <div className="h-full relative flex flex-col border border-gray-300 rounded-lg overflow-hidden bg-white mr-2">
      
      <style>{`
        .table-scroller::-webkit-scrollbar { height: 12px; }
        .table-scroller::-webkit-scrollbar:vertical { display: none !important; }
        .table-scroller::-webkit-scrollbar-track:horizontal { background: #f3f4f6; border-top: 1px solid #e5e7eb; }
        .table-scroller::-webkit-scrollbar-thumb:horizontal { background-color: #d1d5db; border-radius: 6px; border: 2px solid #f3f4f6; }
        .table-scroller::-webkit-scrollbar-thumb:horizontal:hover { background-color: #9ca3af; }
      `}</style>

      {/* Main Container */}
      <div className="flex-1 min-h-0 relative bg-white" ref={containerRef}>
        {viewportSize.height > 0 && viewportSize.width > 0 && (
          <List
            ref={listRef}
            className="table-scroller outline-none"
            innerElementType={InnerElement}
            height={viewportSize.height}
            width={viewportSize.width}
            itemCount={filteredIndices.length === 0 ? 1 : filteredIndices.length}
            itemSize={ITEM_SIZE}
            overscanCount={3}
            onScroll={onScroll}
            itemData={itemData}
          >
            {Row}
          </List>
        )}
      </div>

      {/* Footer Area */}
      <div className="flex-none bg-gray-100 px-2 py-1 text-xs text-gray-600 border-t border-gray-300 z-10">
        {filteredIndices.length} rows {(searchColumns?.length > 0) ? `(${searchColumns.length} column${searchColumns.length > 1 ? 's' : ''} selected for search)` : ''}
      </div>
    </div>
    {/* Absolutely positioned native-like custom scrollbar placed on the right */}
    {totalContentHeight + 24 > LIST_HEIGHT && (
      <div 
        className="absolute right-0 top-0 bottom-0 w-2 border-gray-200 z-30 pointer-events-none"
        style={{ top: basicMode ? '0.5rem' : '2.8rem', bottom: '1.5rem', right: '3.5px' }} 
      >
        <div
          onMouseDown={handleThumbMouseDown}
          className={`absolute right-0 w-2 rounded-full transition-colors pointer-events-auto cursor-pointer ${
            isDragging ? 'bg-gray-600' : 'bg-gray-300 hover:bg-gray-500'
          }`}
          style={{
            height: thumbHeight + 22,
            top: thumbTop,
          }}
        />
      </div>
    )}
    </>
    
  );
});

export default TableViewer;